import { useFormContext } from "react-hook-form";
import { EnquiryFormData } from "@/model/schemas";
import { formatAmount } from "@/util/sums";
import { AmountField, ConsentField, SelectField, TextField } from "./EnquiryFields";
import {
  buyingWithOptions,
  employmentOptions,
  optionLabel,
  ownershipOptions,
  propertyTypeOptions,
  timeframeOptions,
} from "./enquiryOptions";

export const DetailsStep = () => {
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <TextField name="firstName" label="First name" placeholder="e.g. Alex" autoComplete="given-name" />
      <TextField name="lastName" label="Last name" placeholder="e.g. Smith" autoComplete="family-name" />
      <TextField
        name="email"
        label="Email address"
        type="email"
        placeholder="e.g. alex@example.com"
        autoComplete="email"
        className="sm:col-span-2"
      />
      <TextField
        name="phone"
        label="Phone number"
        type="tel"
        placeholder="e.g. 0412 345 678"
        autoComplete="tel"
        className="sm:col-span-2"
      />
    </div>
  );
};

export const SituationStep = () => {
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <SelectField name="buyingWith" label="Buying with" options={buyingWithOptions} />
      <SelectField name="employmentStatus" label="Employment status" options={employmentOptions} />
      <AmountField name="annualIncome" label="Combined annual income (before tax)" placeholder="e.g. $180,000" />
      <AmountField name="depositSavings" label="Savings available for a deposit" placeholder="e.g. $150,000" />
      <SelectField
        name="propertyOwnership"
        label="Current property ownership"
        options={ownershipOptions}
        className="sm:col-span-2"
      />
      <AmountField
        name="monthlyDebts"
        label="Existing debts or repayments"
        placeholder="e.g. $1,500 per month"
        helper="An estimate is fine for now."
        className="sm:col-span-2"
      />
    </div>
  );
};

export const GoalsStep = () => {
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <TextField
        name="location"
        label="Where would you like to buy?"
        placeholder="Suburb, city or postcode"
        className="sm:col-span-2"
      />
      <AmountField name="targetPrice" label="Target property price" placeholder="e.g. $1,500,000" />
      <SelectField
        name="timeframe"
        label="When are you looking to buy?"
        placeholder="Select a timeframe"
        options={timeframeOptions}
      />
      <SelectField name="propertyType" label="Property type" options={propertyTypeOptions} />
      <AmountField name="loanApproval" label="Expected home loan approval" placeholder="e.g. $1,100,000" />
      <TextField
        name="priorities"
        label="What matters most?"
        placeholder="More space, location, school catchment…"
        className="sm:col-span-2"
      />
    </div>
  );
};

const reviewBoxClass = "rounded-2xl border border-[#e1e9e9] p-5";

export const ReviewStep = ({ onEdit }: { onEdit: (step: number) => void }) => {
  const { getValues } = useFormContext<EnquiryFormData>();
  const values = getValues();

  const sections = [
    {
      title: "Your details",
      step: 0,
      lines: [`${values.firstName} ${values.lastName}`, values.email, values.phone],
    },
    {
      title: "Your situation",
      step: 1,
      lines: [
        `${optionLabel(buyingWithOptions, values.buyingWith)} · ${optionLabel(employmentOptions, values.employmentStatus)}`,
        `Income ${formatAmount(values.annualIncome)} · Savings ${formatAmount(values.depositSavings)}`,
        optionLabel(ownershipOptions, values.propertyOwnership),
        `Repayments ${formatAmount(values.monthlyDebts)} per month`,
      ],
    },
    {
      title: "Your goals",
      step: 2,
      lines: [
        values.location,
        `${formatAmount(values.targetPrice)} · ${optionLabel(propertyTypeOptions, values.propertyType)}`,
        `${optionLabel(timeframeOptions, values.timeframe)} · Loan approval ${formatAmount(values.loanApproval)}`,
        values.priorities,
      ],
    },
  ];

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {sections.map((section) => (
          <div key={section.title} className={reviewBoxClass}>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-navy">{section.title}</h3>
              <button
                type="button"
                onClick={() => onEdit(section.step)}
                aria-label={`Edit ${section.title.toLowerCase()}`}
                className="cursor-pointer rounded-full bg-[#e1f8f2] px-3 py-1 text-xs font-semibold text-brand-ink-strong transition-colors hover:bg-[#cdf2e8]"
              >
                Edit
              </button>
            </div>
            <div className="mt-3 space-y-1 text-sm leading-relaxed break-words text-slate-600">
              {section.lines.filter(Boolean).map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </div>
        ))}
        <div className={reviewBoxClass}>
          <h3 className="font-semibold text-navy">Next steps</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Our team will review your enquiry and contact you.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <ConsentField name="confirmAccurate">
          I confirm the information is accurate to the best of my knowledge.
        </ConsentField>
        <ConsentField name="agreePrivacy">
          I agree to the{" "}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="font-semibold underline underline-offset-2"
          >
            Privacy Policy
          </a>{" "}
          and to being contacted about my enquiry.
        </ConsentField>
      </div>
    </>
  );
};
